import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, ShieldAlert, AlertTriangle, Bell, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LicenceInstructorPanel — calm overview of every linked pupil's licence status.
 */
export default function LicenceInstructorPanel() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/licence/for-instructor`, { withCredentials: true });
      setData(data);
    } catch {
      setData({ summary: { verified: 0, pending: 0, expired: 0, none: 0 }, pupils: [] });
    }
  };

  useEffect(() => { load(); }, []);

  const request = async (learner_id) => {
    try {
      await axios.post(`${API}/api/licence/request-update`, { learner_id }, { withCredentials: true });
      toast.success("Asked them to refresh.");
    } catch {
      toast.error("Couldn't send request.");
    }
  };

  if (!data) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <p className="text-slate-400">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="licence-instructor-back"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <h1 className="text-base font-extrabold text-slate-900 dark:text-white">Licence Checks</h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        <div className="grid grid-cols-3 gap-2">
          <Pill testid="lic-sum-verified" label="Verified" value={data.summary.verified} tone="emerald" />
          <Pill testid="lic-sum-pending" label="Pending" value={data.summary.pending + data.summary.none} tone="amber" />
          <Pill testid="lic-sum-expired" label="Expired" value={data.summary.expired} tone="red" />
        </div>

        <div className="rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/40 p-3">
          <p className="text-[11px] text-blue-800 dark:text-blue-200 leading-snug">
            Guided admin support — not a legal check. Your pupils always confirm their own details.
          </p>
        </div>

        {data.pupils.length === 0 ? (
          <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 text-center">
            <Users className="w-6 h-6 text-slate-300 mx-auto" />
            <p className="mt-2 text-sm font-bold text-slate-700 dark:text-slate-200">No linked pupils yet</p>
            <p className="text-xs text-slate-500 mt-1">Invite a pupil, and their licence check will appear here.</p>
          </div>
        ) : (
          <ul data-testid="licence-pupils-list" className="space-y-2">
            {data.pupils.map((p) => (
              <PupilRow key={p.learner_id} p={p} onRequest={() => request(p.learner_id)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Pill({ testid, label, value, tone }) {
  const tones = {
    emerald: "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-900/40 text-emerald-900 dark:text-emerald-100",
    amber: "bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-900/40 text-amber-900 dark:text-amber-100",
    red: "bg-red-50 dark:bg-red-900/15 border-red-200 dark:border-red-900/40 text-red-900 dark:text-red-100",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border p-3 text-center ${tones[tone]}`}>
      <p className="text-2xl font-extrabold">{value}</p>
      <p className="text-[10px] uppercase tracking-wider font-bold">{label}</p>
    </div>
  );
}

function PupilRow({ p, onRequest }) {
  const status = p.status;
  const tone =
    status === "verified"
      ? { bg: "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-900/40", icon: <ShieldCheck className="w-4 h-4 text-emerald-600" /> }
      : status === "expired"
      ? { bg: "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/40", icon: <AlertTriangle className="w-4 h-4 text-red-600" /> }
      : { bg: "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700", icon: <ShieldAlert className="w-4 h-4 text-amber-600" /> };

  return (
    <li data-testid={`licence-pupil-${p.learner_id}`} className={`rounded-2xl border p-3 ${tone.bg}`}>
      <div className="flex items-center gap-2">
        {tone.icon}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">{p.name || "Pupil"}</p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {status === "none" ? "No licence uploaded yet" : (
              <>
                {p.licence_number || "—"} · expires {p.expiry_date || "—"}
                {p.expiring_soon && <span className="ml-1 text-amber-600 font-bold">· {p.days_to_expiry}d</span>}
              </>
            )}
          </p>
        </div>
        <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider ${
          status === "verified" ? "bg-emerald-600 text-white" :
          status === "expired" ? "bg-red-600 text-white" :
          "bg-amber-500 text-white"
        }`}>
          {status === "none" ? "pending" : status}
        </span>
      </div>
      <div className="mt-2 flex justify-end">
        <button
          data-testid={`licence-request-${p.learner_id}`}
          onClick={onRequest}
          className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-slate-300 flex items-center gap-1"
        >
          <Bell className="w-3 h-3" /> Request update
        </button>
      </div>
    </li>
  );
}
